import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Save, X, Plus, Image, Palette, Settings, Eye, EyeOff, 
  Trash2, GripVertical, LogOut, Check, Upload
} from 'lucide-react';
import { getSiteSettings, updateSiteSettings, getAdminPartners, createPartner, deletePartner, getAdminSuccessStories, createSuccessStory, deleteSuccessStory } from '../../api/client';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Editable Text Component
const EditableText = ({ value, onChange, className, tag = 'span', placeholder = 'Click to edit...', multiline = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setText(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (text !== value) {
      onChange(text);
    }
  };

  const Tag = tag;

  if (isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setText(value || '');
              setIsEditing(false);
            }
          }}
          className={`${className} bg-yellow-100 border-2 border-yellow-400 rounded px-2 py-1 outline-none resize-none w-full`}
          rows={3}
          placeholder={placeholder}
        />
      );
    }
    return (
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') {
            setText(value || '');
            setIsEditing(false);
          }
        }}
        className={`${className} bg-yellow-100 border-2 border-yellow-400 rounded px-2 py-1 outline-none`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <Tag
      onClick={() => setIsEditing(true)}
      className={`${className} cursor-pointer hover:bg-yellow-100 hover:outline hover:outline-2 hover:outline-yellow-400 hover:outline-dashed rounded transition-all`}
      title="Click to edit"
    >
      {text || <span className="opacity-50">{placeholder}</span>}
    </Tag>
  );
};

// Editable Image Component
const EditableImage = ({ src, onChange, className, aspectRatio = 'aspect-video' }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = sessionStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      onChange(fileUrl);
      toast.success('Image updated!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div 
      className={`${className} relative group cursor-pointer`}
      onClick={() => fileInputRef.current?.click()}
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <span className="text-gray-400">No image</span>
        </div>
      )}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        {uploading ? (
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full"></div>
        ) : (
          <div className="text-white text-center">
            <Image size={32} className="mx-auto mb-2" />
            <span className="text-sm">Click to change</span>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
};

// Color Picker Component
const ColorPicker = ({ color, onChange, label }) => {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={color || '#0E7A5F'}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded border-2 border-white shadow cursor-pointer"
      />
      <span className="text-sm text-white">{label}</span>
    </div>
  );
};

// Add Partner Modal
const AddPartnerModal = ({ onClose, onAdd }) => {
  const [name, setName] = useState('');
  const [logo, setLogo] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = sessionStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setLogo(`${BACKEND_URL}${response.data.url}`);
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = async () => {
    if (!name.trim()) {
      toast.error('Please enter partner name');
      return;
    }
    await onAdd({ name, logo, category: 'Government', visible: true, order: 0 });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200]" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-96 max-w-[90vw]" onClick={e => e.stopPropagation()}>
        <h3 className="text-xl font-bold mb-4">Add New Partner</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Partner Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Enter partner name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Logo</label>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50"
            >
              {uploading ? (
                <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
              ) : logo ? (
                <img src={logo} alt="Logo" className="max-h-full max-w-full object-contain p-2" />
              ) : (
                <div className="text-center text-gray-400">
                  <Upload size={32} className="mx-auto mb-2" />
                  <span>Click to upload logo</span>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          </div>
        </div>
        
        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleAdd} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Add Partner</button>
        </div>
      </div>
    </div>
  );
};

// Add Story Modal
const AddStoryModal = ({ onClose, onAdd }) => {
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState('');
  const [quote, setQuote] = useState('');
  const [author, setAuthor] = useState('');

  const handleAdd = async () => {
    if (!title.trim() || !quote.trim()) {
      toast.error('Please enter title and testimonial');
      return;
    }
    await onAdd({ title, metric, quote, author, visible: true, order: 0 });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200]" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-96 max-w-[90vw]" onClick={e => e.stopPropagation()}>
        <h3 className="text-xl font-bold mb-4">Add Success Story</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g., Halal Cosmetics Expansion" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Key Metric (optional)</label>
            <input type="text" value={metric} onChange={(e) => setMetric(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g., +200% Sales" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Testimonial</label>
            <textarea value={quote} onChange={(e) => setQuote(e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} placeholder="The testimonial text..." />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Author (optional)</label>
            <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g., CEO, Company Name" />
          </div>
        </div>
        
        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleAdd} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Add Story</button>
        </div>
      </div>
    </div>
  );
};

// Main Live Editor Component
export const LiveEditor = () => {
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const [settings, setSettings] = useState({});
  const [partners, setPartners] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [showAddStory, setShowAddStory] = useState(false);
  const [showColorPanel, setShowColorPanel] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsData, partnersData, storiesData] = await Promise.all([
        getSiteSettings(),
        getAdminPartners(),
        getAdminSuccessStories()
      ]);
      setSettings(settingsData);
      setPartners(partnersData.partners || []);
      setStories(storiesData.stories || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    const keys = key.split('.');
    const newSettings = JSON.parse(JSON.stringify(settings));
    let current = newSettings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSiteSettings(settings);
      setHasChanges(false);
      toast.success('All changes saved!');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPartner = async (partnerData) => {
    try {
      await createPartner(partnerData);
      const data = await getAdminPartners();
      setPartners(data.partners || []);
      toast.success('Partner added!');
    } catch (error) {
      toast.error('Failed to add partner');
    }
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await deletePartner(id);
      setPartners(partners.filter(p => p.id !== id));
      toast.success('Partner deleted!');
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  const handleAddStory = async (storyData) => {
    try {
      await createSuccessStory(storyData);
      const data = await getAdminSuccessStories();
      setStories(data.stories || []);
      toast.success('Story added!');
    } catch (error) {
      toast.error('Failed to add story');
    }
  };

  const handleDeleteStory = async (id) => {
    if (!window.confirm('Delete this story?')) return;
    try {
      await deleteSuccessStory(id);
      setStories(stories.filter(s => s.id !== id));
      toast.success('Story deleted!');
    } catch (error) {
      toast.error('Failed to delete story');
    }
  };

  const handleExit = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Exit anyway?')) return;
    navigate('/admin/dashboard');
  };

  const primaryColor = settings.colors?.primary || '#0E7A5F';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <div className="animate-spin h-12 w-12 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading Live Editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ '--primary-color': primaryColor }}>
      {/* Top Toolbar */}
      <div className="fixed top-0 left-0 right-0 bg-gray-900 text-white px-4 py-3 flex items-center justify-between z-[100] shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={handleExit} className="flex items-center gap-2 hover:bg-gray-800 px-3 py-2 rounded-lg">
            <X size={20} />
            Exit Editor
          </button>
          <span className="text-gray-400">|</span>
          <span className="text-sm">Click on any text or image to edit it directly</span>
        </div>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowColorPanel(!showColorPanel)} 
            className="flex items-center gap-2 hover:bg-gray-800 px-3 py-2 rounded-lg"
          >
            <Palette size={20} />
            Colors
          </button>
          
          {hasChanges && (
            <span className="px-2 py-1 bg-yellow-500 text-black text-xs rounded-full">Unsaved</span>
          )}
          
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              hasChanges ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
            ) : (
              <Save size={20} />
            )}
            {saving ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>

      {/* Color Panel */}
      {showColorPanel && (
        <div className="fixed top-16 right-4 bg-gray-800 rounded-lg p-4 shadow-xl z-[100]">
          <h3 className="text-white font-medium mb-3">Theme Colors</h3>
          <div className="space-y-3">
            <ColorPicker 
              color={settings.colors?.primary} 
              onChange={(v) => updateSetting('colors.primary', v)} 
              label="Primary Color" 
            />
            <ColorPicker 
              color={settings.colors?.primaryHover} 
              onChange={(v) => updateSetting('colors.primaryHover', v)} 
              label="Hover Color" 
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="pt-16">
        {/* Hero Section */}
        <section className="relative min-h-[600px] overflow-hidden bg-gray-900">
          {/* Background Image */}
          <div className="absolute inset-0">
            <EditableImage
              src={settings.heroImage}
              onChange={(v) => updateSetting('heroImage', v)}
              className="w-full h-full"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
          
          {/* Content */}
          <div className="relative z-10 container mx-auto px-6 py-32 text-white">
            {/* Logo */}
            <div className="mb-8">
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: primaryColor }}
                >
                  {(settings.nav?.logoText || 'U')[0]}
                </div>
                <EditableText
                  value={settings.nav?.logoText}
                  onChange={(v) => updateSetting('nav.logoText', v)}
                  className="font-semibold text-lg"
                  placeholder="ASEAN Business Forum"
                />
              </div>
            </div>

            {/* Tags */}
            <div className="flex gap-2 mb-6">
              <EditableText
                value={settings.heroLocation}
                onChange={(v) => updateSetting('heroLocation', v)}
                className="px-3 py-1 bg-white/20 rounded-full text-sm"
                placeholder="Location"
              />
              <EditableText
                value={settings.heroYear}
                onChange={(v) => updateSetting('heroYear', v)}
                className="px-3 py-1 bg-white/20 rounded-full text-sm"
                placeholder="Year"
              />
              <EditableText
                value={settings.heroFeature}
                onChange={(v) => updateSetting('heroFeature', v)}
                className="px-3 py-1 rounded-full text-sm"
                style={{ backgroundColor: primaryColor }}
                placeholder="Feature Tag"
              />
            </div>
            
            <EditableText
              value={settings.heroTitle}
              onChange={(v) => updateSetting('heroTitle', v)}
              tag="h1"
              className="text-5xl md:text-7xl font-bold mb-6 max-w-3xl"
              placeholder="Main Title"
            />
            
            <EditableText
              value={settings.heroSubtitle}
              onChange={(v) => updateSetting('heroSubtitle', v)}
              tag="p"
              className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl"
              placeholder="Subtitle text here..."
              multiline
            />
            
            <div className="flex gap-4">
              <button className="px-6 py-3 rounded-lg font-medium text-white" style={{ backgroundColor: primaryColor }}>
                <EditableText
                  value={settings.heroCtaPrimary}
                  onChange={(v) => updateSetting('heroCtaPrimary', v)}
                  placeholder="Primary Button"
                />
              </button>
              <button className="px-6 py-3 border-2 border-white rounded-lg font-medium text-white hover:bg-white/10">
                <EditableText
                  value={settings.heroCtaSecondary}
                  onChange={(v) => updateSetting('heroCtaSecondary', v)}
                  placeholder="Secondary Button"
                />
              </button>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 text-white" style={{ backgroundColor: primaryColor }}>
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <EditableText value={settings.stats?.exhibitors} onChange={(v) => updateSetting('stats.exhibitors', v)} tag="div" className="text-4xl font-bold" placeholder="300+" />
                <EditableText value={settings.stats?.exhibitorsLabel || 'Exhibitors'} onChange={(v) => updateSetting('stats.exhibitorsLabel', v)} tag="div" className="text-white/80" placeholder="Label" />
              </div>
              <div>
                <EditableText value={settings.stats?.countries} onChange={(v) => updateSetting('stats.countries', v)} tag="div" className="text-4xl font-bold" placeholder="50+" />
                <EditableText value={settings.stats?.countriesLabel || 'Countries'} onChange={(v) => updateSetting('stats.countriesLabel', v)} tag="div" className="text-white/80" placeholder="Label" />
              </div>
              <div>
                <EditableText value={settings.stats?.delegations} onChange={(v) => updateSetting('stats.delegations', v)} tag="div" className="text-4xl font-bold" placeholder="150+" />
                <EditableText value={settings.stats?.delegationsLabel || 'Trade Delegations'} onChange={(v) => updateSetting('stats.delegationsLabel', v)} tag="div" className="text-white/80" placeholder="Label" />
              </div>
            </div>
          </div>
        </section>

        {/* Gallery/Previous Events Section - Read Only Display */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-6">
            <h2 className="text-4xl font-bold text-white text-center mb-4">Our Journey</h2>
            <p className="text-gray-400 text-center mb-8">
              Previous event data from Annual Book (not editable here - managed separately)
            </p>
            
            {/* Previous Events Timeline */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {[
                { year: '2014', location: 'Singapore', countries: 12, attendees: 325 },
                { year: '2015', location: 'India', countries: 29, attendees: 1400 },
                { year: '2016', location: 'Croatia', countries: 35, attendees: 1800 },
                { year: '2018', location: 'Turkey', countries: 45, attendees: 2500 },
              ].map((event, idx) => (
                <div key={event.year} className="bg-gray-800 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>{event.year}</div>
                  <div className="text-white font-semibold mb-3">{event.location}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-900 rounded p-2">
                      <div className="text-white font-bold">{event.countries}+</div>
                      <div className="text-gray-500">Countries</div>
                    </div>
                    <div className="bg-gray-900 rounded p-2">
                      <div className="text-white font-bold">{event.attendees}+</div>
                      <div className="text-gray-500">Attendees</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-gray-500 text-center text-sm">
              Gallery slideshow with images from Annual Book displays on the public website
            </p>
          </div>
        </section>

        {/* Success Stories Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <EditableText
              value={settings.success?.title || 'Success Stories'}
              onChange={(v) => updateSetting('success.title', v)}
              tag="h2"
              className="text-4xl font-bold text-center mb-12"
              placeholder="Section Title"
            />
            
            <div className="grid md:grid-cols-3 gap-8">
              {stories.filter(s => s.visible !== false).map((story) => (
                <div key={story.id} className="relative group bg-white p-8 rounded-2xl border border-gray-200 shadow-lg">
                  <button
                    onClick={() => handleDeleteStory(story.id)}
                    className="absolute top-4 right-4 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={14} />
                  </button>
                  <h3 className="text-xl font-bold mb-3">{story.title}</h3>
                  {story.metric && (
                    <div className="text-3xl font-bold mb-4" style={{ color: primaryColor }}>{story.metric}</div>
                  )}
                  <p className="text-gray-600 italic mb-4">{story.quote}</p>
                  {story.author && <div className="text-sm text-gray-500">— {story.author}</div>}
                </div>
              ))}
              
              {/* Add Story Button */}
              <button
                onClick={() => setShowAddStory(true)}
                className="p-8 rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-green-500 hover:text-green-500 transition-colors min-h-[200px]"
              >
                <Plus size={40} />
                <span className="mt-2">Add Success Story</span>
              </button>
            </div>
          </div>
        </section>

        {/* Destinations Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <EditableText
              value={settings.destinations?.title || 'Host Destinations'}
              onChange={(v) => updateSetting('destinations.title', v)}
              tag="h2"
              className="text-4xl font-bold text-center mb-12"
              placeholder="Section Title"
            />
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Batam */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                <EditableImage
                  src={settings.destinations?.batam?.image}
                  onChange={(v) => updateSetting('destinations.batam.image', v)}
                  className="h-64"
                />
                <div className="p-6">
                  <EditableText
                    value={settings.destinations?.batam?.title || 'Batam'}
                    onChange={(v) => updateSetting('destinations.batam.title', v)}
                    tag="h3"
                    className="text-2xl font-bold mb-3"
                    placeholder="Destination Name"
                  />
                  <EditableText
                    value={settings.destinations?.batam?.description}
                    onChange={(v) => updateSetting('destinations.batam.description', v)}
                    tag="p"
                    className="text-gray-600"
                    placeholder="Description..."
                    multiline
                  />
                </div>
              </div>
              
              {/* Bintan */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                <EditableImage
                  src={settings.destinations?.bintan?.image}
                  onChange={(v) => updateSetting('destinations.bintan.image', v)}
                  className="h-64"
                />
                <div className="p-6">
                  <EditableText
                    value={settings.destinations?.bintan?.title || 'Bintan'}
                    onChange={(v) => updateSetting('destinations.bintan.title', v)}
                    tag="h3"
                    className="text-2xl font-bold mb-3"
                    placeholder="Destination Name"
                  />
                  <EditableText
                    value={settings.destinations?.bintan?.description}
                    onChange={(v) => updateSetting('destinations.bintan.description', v)}
                    tag="p"
                    className="text-gray-600"
                    placeholder="Description..."
                    multiline
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="container mx-auto px-6 text-center">
            <EditableText
              value={settings.contact?.title || 'Contact Us'}
              onChange={(v) => updateSetting('contact.title', v)}
              tag="h2"
              className="text-4xl font-bold mb-8"
              placeholder="Section Title"
            />
            
            <div className="flex flex-wrap justify-center gap-8">
              <div className="flex items-center gap-3">
                <span className="text-gray-500">WhatsApp:</span>
                <EditableText
                  value={settings.contact?.whatsapp}
                  onChange={(v) => updateSetting('contact.whatsapp', v)}
                  className="font-medium"
                  placeholder="+62 812 3456 7890"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-500">Email:</span>
                <EditableText
                  value={settings.contact?.email}
                  onChange={(v) => updateSetting('contact.email', v)}
                  className="font-medium"
                  placeholder="info@example.com"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-500">Location:</span>
                <EditableText
                  value={settings.contact?.location}
                  onChange={(v) => updateSetting('contact.location', v)}
                  className="font-medium"
                  placeholder="City, Country"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 text-white" style={{ backgroundColor: primaryColor }}>
          <div className="container mx-auto px-6 text-center">
            <EditableText
              value={settings.footer?.text}
              onChange={(v) => updateSetting('footer.text', v)}
              className="mb-2"
              placeholder="Footer text"
            />
            <EditableText
              value={settings.footer?.copyright}
              onChange={(v) => updateSetting('footer.copyright', v)}
              className="text-white/70 text-sm"
              placeholder="© 2026 Your Company"
            />
          </div>
        </footer>
      </div>

      {/* Modals */}
      {showAddPartner && <AddPartnerModal onClose={() => setShowAddPartner(false)} onAdd={handleAddPartner} />}
      {showAddStory && <AddStoryModal onClose={() => setShowAddStory(false)} onAdd={handleAddStory} />}
    </div>
  );
};
